import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {CircleState, CircleStateComponent} from "../../util/design/circle-state/circle-state.component";
import {
  faArrowDownToBracket,
  faArrowUpRightFromSquare,
  faCircleQuestion,
  faClipboardList,
  faDownload
} from "@fortawesome/pro-solid-svg-icons";
import {PopoutContentService} from "../../../services/popout/popout.service";
import {StateConfigService} from "../../../services/state/state-config.service";
import {DatePipe, NgClass} from "@angular/common";
import {RouterLink} from "@angular/router";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {BicButtonComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {SkeletonLoadingDirective} from "../../../directives/skeleton-loading.directive";

/** Statuswerte, die der Bericht kennt. */
type ReportStatus = 'success' | 'warning' | 'error' | 'unknown';


@Component({
  selector: 'app-overview-reports',
    templateUrl: './overview-report.component.html',
    styleUrls: ['./overview-report.component.scss'],
  host: {
    class: 'w-full'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CircleStateComponent,
    DatePipe,
    FontAwesomeModule,
    BicButtonComponent,
    TooltipDirective,
    SkeletonLoadingDirective,
    NgClass,
    RouterLink,
  ]
})
export class OverviewReportComponent implements OnInit {
  private popoutContentService = inject(PopoutContentService);
  @Input() loading = false;
  @Input() checkType!: 'LOI' | 'BIM_BASE' | 'QUALITY' | 'HOAI' | 'MY_CHECK'
  @Input() info1: string | number = '';
  /**
   * Optionaler Tooltip auf {@link info1}. Gebraucht, sobald dort nicht der Name des Prüflaufs
   * steht, sondern ein frei vergebener Berichtsname – dann nennt der Tooltip den Prüflauf, damit
   * die Zuordnung nicht verloren geht. Leer = kein Tooltip (bisheriges Verhalten).
   */
  @Input() info1Tooltip = '';
  @Input() checkerName: string = '';
  @Input() info2: string | number = '';
  protected stateConfigService = inject(StateConfigService);
  @Input() date: Date | undefined = undefined;
  @Input() rules: string = "0";
  @Input() ruleTypeLabel: string = '';
  @Input() fileCount: string | number = '';
  /** Name der einzigen geprüften Datei; wird statt der Anzahl gezeigt, wenn fileCount === 1. */
  @Input() fileName: string = '';

  @Input() completedValue: number = 0;
  @Input() warningValue: number = 0;
  @Input() errorValue: number = 0;

  /**
   * EIN Gesamtergebnis statt der drei Verteilungskreise (#2317).
   *
   * <p>Die drei Kreise zeigen eine Statusverteilung über die Einzelergebnisse — bei IDS gibt es die
   * nicht: dort ist der Spec-Status ein Boolean, und die Overview musste `warning = 0` und
   * `error = 100 − completed` erfinden. Das las sich als „50 % erfüllt, 50 % nicht erfüllt" statt
   * als ein Ergebnis von 50 %.
   *
   * <p>Bewusst ein eigener Input und nicht ein weiterer `checkType`: der steuert auch das
   * Kopf-Layout (Prüflauf/myCheck/Prüfregeltyp), ein neuer Wert dort hätte es mit abgeschaltet.
   *
   * <p>`null` = wie bisher drei Kreise. `percent === null` heißt „nicht bewertbar" und zeigt einen
   * grauen Kreis ohne Wert.
   */
  @Input() singleResult: { percent: number | null; status: ReportStatus } | null = null;

  /** Kreisfarbe des Gesamtergebnisses. */
  singleResultState(): CircleState {
    if (this.loading) return CircleState.LOAD;
    switch (this.singleResult?.status) {
      case 'success':
        return CircleState.GREEN;
      case 'warning':
        return CircleState.YELLOW;
      case 'error':
        return CircleState.RED;
      default:
        // „Unbekannt"/nicht bewertbar: der Kreis bleibt leer, der Hinweistext des Berichts
        // erklärt die Ursache.
        return CircleState.LOAD;
    }
  }

  /** Tooltip des Gesamtergebnisses – benennt die Stufe, die die Farbe meint. */
  singleResultTooltip(): string {
    if (this.loading) return 'Lädt';
    switch (this.singleResult?.status) {
      case 'success':
        return 'Erfüllt';
      case 'warning':
        return 'Teilweise erfüllt';
      case 'error':
        return 'Nicht erfüllt';
      default:
        return 'Nicht bewertbar';
    }
  }

  @Input() fileDeactivated: boolean = false;
  @Input() fileId: string = '';
  @Input() activateDownload: boolean = false;
  @Input() activateOpen: boolean = false;
  @Input() showOpenFile: boolean = true;
  /** Aktions-Buttons (Bericht/Öffnen/Logs) ausblenden, wenn sie an anderer Stelle (z. B. Topbar) gerendert werden. */
  @Input() showActions: boolean = true;

  /** RouterLink-Kommandos für `info1` (myCheck); `null` = nicht klickbar (kein Ziel bekannt). */
  @Input() myCheckLink: any[] | null = null;
  /**
   * Prüfregelset(s) mit RouterLink-Kommandos; nicht leer → `info2` wird durch die Namen ersetzt.
   *
   * `routerLink: null` = zentral bereitgestellt: der Name wird als reiner Text mit Badge
   * gerendert, nicht als Link. Bereitgestellte Regelwerke sind nur zum Ausführen oder
   * Duplizieren gedacht; ein Editor-Link daraus überschrieb sonst die zentrale Vorlage.
   */
  @Input() rulesetLinks: Array<{ id: string; name: string; routerLink: any[] | null }> = [];

  @Output() download: EventEmitter<void> = new EventEmitter<void>();
  @Output() open: EventEmitter<void> = new EventEmitter<void>();
  @Output() showLogs: EventEmitter<void> = new EventEmitter<void>();
  protected readonly CircleState = CircleState;

  ngOnInit(): void {
  }

  openFile() {
    let baseUrl = window.location.origin + '/projects/' + this.stateConfigService.selectedProject.id + '/files/' + this.fileId;
    this.popoutContentService.open(baseUrl, '', true);
  }

  downloadPressed(){
    this.download.emit();
  }

  showLogsPressed(){
    this.showLogs.emit();
  }

  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly isFinite = isFinite;
  protected readonly Math = Math;
    protected readonly faCircleQuestion = faCircleQuestion;
  protected readonly faDownload = faDownload;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faClipboardList = faClipboardList;
}
